import React, { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import toast from "react-hot-toast";
import { Wrench, ChevronDown, Minimize2 } from "lucide-react";
import { MOCK_REPORTS, MockReportPreset } from "../../pages/reports/mockReportsData";

const ReportDevAutofill: React.FC = () => {
  const isDev = process.env.NODE_ENV === "development" || process.env.REACT_APP_SHOW_DEV_TOOLS === "true";
  const { setValue } = useFormContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const [devPanelPos, setDevPanelPos] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  const dragStartCoords = useRef({ x: 0, y: 0 });
  const dragOffset = useRef({ x: 0, y: 0 });
  const dragTotalDistance = useRef(0);

  const handlePointerDown = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    const isGrip = target.closest(".drag-handle");
    const isBubble = !isExpanded && target.closest(".dev-bubble");

    if (isGrip || isBubble) {
      setIsDragging(true);
      dragStartCoords.current = { x: e.clientX, y: e.clientY };
      dragOffset.current = {
        x: e.clientX - devPanelPos.x,
        y: e.clientY - devPanelPos.y,
      };
      dragTotalDistance.current = 0;
      target.setPointerCapture(e.pointerId);
    }
  };

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const dx = e.clientX - dragStartCoords.current.x;
      const dy = e.clientY - dragStartCoords.current.y;
      dragTotalDistance.current = Math.sqrt(dx * dx + dy * dy);

      setDevPanelPos({
        x: e.clientX - dragOffset.current.x,
        y: e.clientY - dragOffset.current.y,
      });
    };

    const handlePointerUp = (e: PointerEvent) => {
      setIsDragging(false);
      try {
        const target = e.target as HTMLElement;
        target.releasePointerCapture(e.pointerId);
      } catch (err) {
        // Safe check
      }

      if (dragTotalDistance.current < 5) {
        setIsExpanded(prev => !prev);
      }
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
    };
  }, [isDragging, devPanelPos]);

  if (!isDev) return null;

  const performAutofill = (sample: MockReportPreset) => {
    setValue("title", sample.title, { shouldDirty: true, shouldValidate: true });
    setValue("scope", sample.scope, { shouldDirty: true, shouldValidate: true });
    setValue("category", sample.category, { shouldDirty: true, shouldValidate: true });
    setValue("severity", sample.severity, { shouldDirty: true, shouldValidate: true });
    setValue("description", sample.description, { shouldDirty: true, shouldValidate: true });
    setValue("instructions", sample.instructions, { shouldDirty: true, shouldValidate: true });
    setValue("impact", sample.impact, { shouldDirty: true, shouldValidate: true });
    setValue("fix", sample.fix, { shouldDirty: true, shouldValidate: true });
    setValue("additionalInfo", sample.additionalInfo, { shouldDirty: true, shouldValidate: true });

    toast.success(`⚡ Report Autofilled: "${sample.title}"!`);
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value;
    if (val === "") return;

    if (val === "random") {
      const randomIdx = Math.floor(Math.random() * MOCK_REPORTS.length);
      performAutofill(MOCK_REPORTS[randomIdx]);
    } else {
      const idx = parseInt(val, 10);
      if (MOCK_REPORTS[idx]) {
        performAutofill(MOCK_REPORTS[idx]);
      }
    }
    e.target.value = "";
  };

  return (
    <div
      style={{
        transform: `translate(${devPanelPos.x}px, ${devPanelPos.y}px)`,
        touchAction: "none",
      }}
      onPointerDown={handlePointerDown}
      className="fixed bottom-6 right-6 z-[999999] select-none shrink-0"
    >
      {!isExpanded ? (
        /* Collapsed Mini-Bubble FAB */
        <div
          className={`dev-bubble w-9 h-9 bg-gradient-to-tr from-emerald-600 to-teal-500 rounded-full flex items-center justify-center cursor-grab shadow-lg border border-emerald-400/30 hover:scale-105 active:scale-95 transition duration-150 relative ${
            isDragging ? "cursor-grabbing ring-2 ring-emerald-500/30" : ""
          }`}
          title="🛠️ Report Dev Wizard (Click to open, Drag to move)"
        >
          <Wrench size={13} className="text-white animate-pulse" />
          <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-indigo-400 rounded-full ring-1 ring-white" />
        </div>
      ) : (
        /* Sleek, Ultra-Compact open Selector Widget */
        <div
          className={`bg-white/95 backdrop-blur-md border border-emerald-150 p-2 rounded-xl shadow-2xl w-[240px] flex flex-col gap-1.5 transition-all duration-200 ${
            isDragging ? "ring-2 ring-emerald-500/20 shadow-emerald-100/50" : ""
          }`}
        >
          {/* Header Drag Bar */}
          <div className="flex items-center gap-1 pb-1 border-b border-emerald-50/60 select-none shrink-0">
            <div
              className={`drag-handle text-emerald-300 font-extrabold text-[10px] tracking-tight flex items-center justify-center mr-0.5 select-none ${
                isDragging ? "cursor-grabbing" : "cursor-grab"
              }`}
              title="Drag to reposition panel"
            >
              ⣿
            </div>
            <div className="flex items-center justify-center w-3.5 h-3.5 rounded bg-emerald-600 text-white shadow-sm shrink-0">
              <Wrench size={8} className="text-white" />
            </div>
            <div className="flex-1">
              <span className="font-extrabold text-emerald-950 text-[8.5px] leading-none block">🛠️ Report Dev Helper</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsExpanded(false);
              }}
              className="w-4.5 h-4.5 p-0.5 flex items-center justify-center rounded text-emerald-400 hover:text-emerald-600 hover:bg-emerald-50 transition shrink-0"
              title="Minimize panel"
            >
              <Minimize2 size={8} />
            </button>
          </div>

          {/* Selector Dropdown */}
          <div className="relative flex flex-col gap-1">
            <select
              defaultValue=""
              onChange={handleSelectChange}
              className="w-full h-6.5 pl-1.5 pr-5 bg-white border border-emerald-150 rounded-lg text-[9px] font-bold text-emerald-900 focus:outline-none focus:border-emerald-500 hover:border-emerald-300 transition cursor-pointer shadow-sm appearance-none"
            >
              <option value="" disabled className="text-emerald-400">-- Choose Pre-fill Sample (50) --</option>
              {MOCK_REPORTS.map((preset, idx) => (
                <option key={idx} value={idx}>
                  {idx + 1}. [{preset.severity.category}] {preset.title.length > 25 ? preset.title.substring(0, 25) + "..." : preset.title}
                </option>
              ))}
              <option value="random" className="font-bold text-indigo-600">🎲 Random Autofill...</option>
            </select>
            <div className="absolute top-2 right-0 pr-1.5 flex items-center pointer-events-none text-emerald-500">
              <ChevronDown size={8} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportDevAutofill;
